import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash-es';
import { waitForElement } from '@namespace/helpers';
import { UserContext } from '@namespace/user';
import { CASINO_TYPE } from '../../constants';
import {
  gamesStoreActions,
  useGetIsLobbyDataLoaded,
  useIsCmsPragmaticLiveDataLoaded,
  useProvider,
  useUnsortedGamesByProvider
} from '../../store';
import { useGetPragmaticData } from '../useGetPragmaticData';

const { SET_PRAGMATIC_GAMES_LIVE_DATA, GET_PRAGMATIC_INNER_GAMES_IDS } =
  gamesStoreActions;

export const usePragmaticData = ({
  casinoType,
  lobbyCasinoType,
  defaultFeedName
}) => {
  const [user] = useContext(UserContext);
  const refPragmaticData = useRef([]);
  const { activeWallet = {} } = user;
  const { currency } = activeWallet;
  const {
    defaultCurrency,
    forceCurrency,
    pragmaticScriptSrc,
    pragmaticWSUrl,
    casinoId,
    pragmaticProviderId,
    isPragmaticEnabled
  } = useGetPragmaticData();

  const pragmaticProvider = useProvider(pragmaticProviderId);
  const isCmsPragmaticLiveDataLoaded = useIsCmsPragmaticLiveDataLoaded();

  const isFeedLoaded = useGetIsLobbyDataLoaded(
    defaultFeedName,
    lobbyCasinoType || casinoType
  );

  const [isPragmaticScriptLoaded, setIsPragmaticScriptLoaded] = useState(false);

  const isSubscribingStartedRef = useRef(false);
  const isCmsPragmaticLiveInfoQueriedRef = useRef(false);

  const pragmaticGames = Object.values(
    useUnsortedGamesByProvider(pragmaticProviderId)
  );

  const onSetPragmaticData = useCallback(
    throttle((data, callback) => {
      callback();
      SET_PRAGMATIC_GAMES_LIVE_DATA(data);
    }, 3000),
    [casinoType]
  );

  const preparedCurrency = forceCurrency || currency || defaultCurrency;

  const onMessage = useCallback(
    (data) => {
      if (data?.tableKey) {
        window.dga.subscribe(casinoId, data.tableKey, preparedCurrency);
      }

      refPragmaticData.current.push({
        ...(data ?? {})
      });
      onSetPragmaticData(refPragmaticData.current, () => {
        refPragmaticData.current = [];
      });
    },
    [
      onSetPragmaticData,
      refPragmaticData,
      casinoId,
      defaultCurrency,
      preparedCurrency,
      casinoType
    ]
  );

  const subscribeToWS = useCallback(() => {
    if (
      !window.dga ||
      !casinoId ||
      !pragmaticWSUrl ||
      isSubscribingStartedRef.current
    ) {
      return;
    }
    isSubscribingStartedRef.current = true;
    window.dga.connect(pragmaticWSUrl);
    window.dga.onConnect = () => {
      window.dga.available(casinoId);
      window.dga.onMessage = onMessage;
    };
  }, [currency, defaultCurrency, pragmaticGames, casinoId, pragmaticWSUrl]);

  useEffect(() => {
    if (
      isPragmaticEnabled &&
      !isCmsPragmaticLiveDataLoaded &&
      isFeedLoaded &&
      pragmaticProvider?.serviceId &&
      pragmaticGames.length &&
      casinoType === CASINO_TYPE.CASINO_LIVE &&
      !isCmsPragmaticLiveInfoQueriedRef.current
    ) {
      GET_PRAGMATIC_INNER_GAMES_IDS(pragmaticProvider.serviceId);
      isCmsPragmaticLiveInfoQueriedRef.current = true;
    }
  }, [
    isPragmaticEnabled,
    isCmsPragmaticLiveDataLoaded,
    isFeedLoaded,
    pragmaticProvider.serviceId,
    pragmaticGames.length,
    casinoType
  ]);

  useEffect(() => {
    if (isPragmaticEnabled && pragmaticScriptSrc) {
      waitForElement('#casino-games-wrapper', { timeout: 200 }).then(() => {
        const script = document.createElement('script');

        script.onload = () => setIsPragmaticScriptLoaded(true);
        script.src = pragmaticScriptSrc;
        document.querySelector('#casino-games-wrapper').appendChild(script);
      });
    }
  }, [isPragmaticEnabled, pragmaticScriptSrc]);

  useEffect(() => {
    (async () => {
      try {
        if (casinoType !== CASINO_TYPE.CASINO_LIVE) {
          return;
        }
        if (!isPragmaticEnabled) {
          return;
        }
        // we have to load CmsPragmaticLiveData for casino-live to associate gameLaunchId with a provider inner gameid
        if (!isCmsPragmaticLiveDataLoaded) {
          return;
        }
        if (!isPragmaticScriptLoaded) {
          return;
        }
        if (!pragmaticGames.length) {
          return;
        }
        if (window.dga?.websocket) {
          return;
        }
        if (!isFeedLoaded) {
          return;
        }
        await subscribeToWS();
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [
    isPragmaticEnabled,
    pragmaticGames.length,
    isCmsPragmaticLiveDataLoaded,
    isPragmaticScriptLoaded,
    casinoType,
    isFeedLoaded
  ]);

  const disconnectWS = () => {
    if (window.dga?.websocket) {
      window.dga.disconnect();
      window.dga = null;
    }

    setIsPragmaticScriptLoaded(false);
  };

  useEffect(() => {
    return () => {
      disconnectWS();
    };
  }, []);
};
